import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Card, CardHeader, CardTitle, CardContent, Input, Button, Alert } from '../components'

export const Login: React.FC = () => {
  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const { sendOtp, verifyOtp } = useAuth()

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    
    if (!email) {
      setError('Please enter your email')
      return
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email')
      return
    }
    
    const success = await sendOtp(email)
    if (!success) {
      setError('Failed to send your Magic Key. Please try again.')
      return
    }

    setSuccess('Your Magic Key is on its way - check your email.')
    setStep('otp')
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!otp) {
      setError('Please enter your Magic Key')
      return
    }

    if (otp.length !== 6) {
      setError('Your Magic Key must be 6 digits')
      return
    }

    const success = await verifyOtp(email, otp)
    if (!success) {
      setError('Invalid or expired Magic Key')
      setOtp('')
    }
  }

  const handleBack = () => {
    setStep('email')
    setOtp('')
    setError('')
    setSuccess('')
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <div className="flex items-center gap-2 mb-6">
        <img src="/conveyo/logo.svg" alt="Conveyo Logo" className="w-10 h-10" />
        <h1 className="logo">
          <span className="logo-conveyo">conveyo</span>
          <span className="logo-dot">.</span>
          <span className="logo-one">one</span>
        </h1>
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">Login / Sign Up</CardTitle>
        </CardHeader>
        <CardContent>
          {step === 'email' ? (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <Input
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                error={error}
                autoFocus
              />
              
              {error && (
                <Alert type="error">
                  {error}
                </Alert>
              )}
              
              <Button type="submit" className="w-full" size="lg">
                Continue
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="text-sm text-gray-600 mb-2">
                🔑 Magic Key sent to: <span className="font-medium">{email}</span>
              </div>

              <Input
                type="text"
                label="Enter your Magic Key"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit Magic Key"
                maxLength={7}
                error={error}
                autoFocus
              />

              {error && (
                <Alert type="error">
                  {error}
                </Alert>
              )}

              {success && (
                <Alert type="success">
                  {success}
                </Alert>
              )}

              <Button type="submit" className="w-full" size="lg">
                Unlock with Magic Key
              </Button>
              
              <Button 
                type="button" 
                variant="secondary" 
                className="w-full" 
                onClick={handleBack}
              >
                Back
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
